import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle2,
  Loader2,
  MessageSquare,
} from "lucide-react";

const API_BASE = "/api";
const WHATSAPP_URL = "http://wa.me/+2348037400677";
const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/Bomtrack+Nigeria+Limited/@6.4686399,3.7062829,494m/data=!3m1!1e3!4m6!3m5!1s0x103bff0050be2343:0xd1c889667a1d33f2!8m2!3d6.4684762!4d3.706028!16s%2Fg%2F11wbvs5t8b!5m1!1e1?hl=en&entry=ttu&g_ep=EgoyMDI2MDYyMy4wIKXMDSoASAFQAw%3D%3D";

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/bomtrack_nigeria_ltd",
    path: "M7.75 2h8.5A5.76 5.76 0 0 1 22 7.75v8.5A5.76 5.76 0 0 1 16.25 22h-8.5A5.76 5.76 0 0 1 2 16.25v-8.5A5.76 5.76 0 0 1 7.75 2Zm0 2A3.75 3.75 0 0 0 4 7.75v8.5A3.75 3.75 0 0 0 7.75 20h8.5A3.75 3.75 0 0 0 20 16.25v-8.5A3.75 3.75 0 0 0 16.25 4h-8.5ZM12 7.25A4.75 4.75 0 1 1 12 16.75 4.75 4.75 0 0 1 12 7.25Zm0 2A2.75 2.75 0 1 0 12 14.75 2.75 2.75 0 0 0 12 9.25Zm5.2-2.45a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/100091821480849",
    path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/bomtrack-nigeria-limited/",
    path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@bomtrack_nigeria_ltd",
    path: "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .6.05.88.14V9.4a6.36 6.36 0 0 0-.88-.06A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V8.56a8.16 8.16 0 0 0 4.77 1.53V6.73c-.35 0-.7-.02-1.04-.04Z",
  },
  {
    name: "X",
    href: "https://x.com/bomtrackng",
    path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z",
  },
];

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function PageHeader() {
  return (
    <section className="pt-28 pb-12 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(219,106,64,0.25) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="section-padding max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-left"
        >
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 text-sm mb-4"
          >
            <Link to="/" className="text-white/60 hover:text-accent transition-colors">
              Home
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-accent font-medium">Contact</span>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-xs mb-4"
          >
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            Get In Touch
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-3xl md:text-4xl font-bold text-white mb-3"
          >
            Contact Us
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-16 h-0.5 bg-accent origin-left mb-4 rounded-full"
          />

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-white/70 max-w-xl text-base"
          >
            Ready to start your project? Reach out to us for a free consultation and quote.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ContactInfo() {
  const emailAddresses = new Set([
    "admin@bomtrackng.com",
    "info@bomtrackng.com",
    "support@bomtrackng.com",
  ]);
  const phoneNumbers = new Set(["+234 803 740 0677"]);

  const contactDetails = [
    {
      icon: MapPin,
      title: "Visit Our Office",
      details: [
        "Shop D-203, 2nd Floor, Left Wing,",
        "Awoyaya Complex, Awoyaya,",
        "Ibeju-Lekki, Lagos State, Nigeria",
      ],
      color: "bg-primary",
      accentColor: "bg-accent",
      href: GOOGLE_MAPS_URL,
      linkLabel: "Open Bomtrack office location in Google Maps",
    },
    {
      icon: Phone,
      title: "Contact Us",
      details: ["+234 803 740 0677"],
      color: "bg-primary",
      accentColor: "bg-green-500",
      showSocialLinks: true,
    },
    {
      icon: Mail,
      title: "Email Us",
      details: ["admin@bomtrackng.com", "info@bomtrackng.com", "support@bomtrackng.com"],
      color: "bg-primary",
      accentColor: "bg-blue-500",
    },
    {
      icon: Clock,
      title: "Working Hours",
      details: ["Monday - Friday: 8:00AM - 5:00PM", "Saturday: 9:00AM - 2:00PM"],
      color: "bg-primary",
      accentColor: "bg-purple-500",
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="section-padding max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 rounded-full text-accent font-medium text-sm mb-4">
            <MessageSquare className="w-4 h-4" />
            Contact Information
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-primary">Ways to Reach Us</h2>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactDetails.map((item, index) => (
            <AnimatedSection key={item.title} delay={index * 0.1}>
              <div
                role={item.href ? "link" : undefined}
                tabIndex={item.href ? 0 : undefined}
                aria-label={item.linkLabel}
                onClick={() => {
                  if (item.href) {
                    window.open(item.href, "_blank", "noopener,noreferrer");
                  }
                }}
                onKeyDown={(e) => {
                  if (item.href && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    window.open(item.href, "_blank", "noopener,noreferrer");
                  }
                }}
                className={`group bg-neutral-50 rounded-2xl p-6 border border-neutral-100 hover:border-accent/30 hover:shadow-xl hover:shadow-accent/5 transition-all duration-300 h-full ${
                  item.href ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent/30" : ""
                }`}
              >
                <div
                  className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                >
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-base font-semibold text-primary mb-3">{item.title}</h3>
                <ul className="space-y-1.5">
                  {item.details.map((detail, i) => (
                    <li key={i} className="text-neutral-600 text-sm leading-relaxed">
                      {emailAddresses.has(detail) ? (
                        <a
                          href={`mailto:${detail}`}
                          className="hover:text-accent transition-colors"
                        >
                          {detail}
                        </a>
                      ) : phoneNumbers.has(detail) ? (
                        <a
                          href={WHATSAPP_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-accent transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </li>
                  ))}
                </ul>
                {item.showSocialLinks && (
                  <div className="flex flex-wrap gap-2.5 mt-5 pt-4 border-t border-neutral-200">
                    {socialLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit Bomtrack on ${link.name}`}
                        title={link.name}
                        className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-accent hover:text-white transition-colors duration-300"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path d={link.path} />
                        </svg>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => null);
      
      if (!response.ok && !data) {
        throw new Error("Failed to submit message");
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError(
        <>
          We're experiencing technical difficulties. Please contact us directly at{" "}
          <a href="mailto:info@bomtrackng.com" className="font-medium underline">
            info@bomtrackng.com
          </a>{" "}
          or call{" "}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline"
          >
            +234 803 740 0677
          </a>
          .
        </>
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-neutral-50">
      <div className="section-padding max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          <AnimatedSection>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-accent rounded-full" />
              <span className="text-accent font-medium text-sm uppercase tracking-wide">
                Send Message
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6">Get a Free Quote</h2>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-100 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-primary mb-2">Message Sent!</h3>
                <p className="text-neutral-600 mb-6">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-accent font-medium hover:underline"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                      placeholder="+234 803 740 0677"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Service Needed *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all bg-white"
                    >
                      <option value="">Select a service</option>
                      <option value="Boundary Survey">Boundary Survey</option>
                      <option value="Topographic Survey">Topographic Survey</option>
                      <option value="Construction Survey">Construction Survey</option>
                      <option value="GIS Services">GIS Services</option>
                      <option value="Subdivision Survey">Subdivision Survey</option>
                      <option value="Cadastral Survey">Cadastral Survey</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent hover:bg-accent/90 text-white py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="h-full min-h-[400px] bg-neutral-200 rounded-2xl overflow-hidden relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d333.87684078344716!2d3.705988068909763!3d6.468560228395248!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103bff0050be2343%3A0xd1c889667a1d33f2!2sBomtrack%20Nigeria%20Ltd!5e1!3m2!1sen!2sus!4v1777297164287!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "400px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bomtrack Office Location"
                className="absolute inset-0"
              />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <main className="min-h-screen">
      <PageHeader />
      <ContactInfo />
      <ContactForm />
    </main>
  );
}
